import React, { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './MyAdCardActions.styled';
import type {
  MyAdCardAction,
  MyAdCardActionsConfig,
} from './MyAdCardActions.types';
import type { Ad } from '@/api/types/common.types';
import { MoreHorizontalIcon } from '@/components/UI/Icons/Icons';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { useResponsive } from '@/hooks/useResponsive';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';

type Props = {
  ad: Ad;
  actions: MyAdCardActionsConfig;
};

export const MyAdCardActions = ({ ad, actions }: Props) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const [isMenuOpen, toggleMenu] = useToggleOpenState(false);
  const { primaryAction, menuActions = [] } = actions;
  const hasMenu = menuActions.length > 0;

  if (!primaryAction && !hasMenu) return null;

  function stopCardNavigation(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handlePrimaryClick(event: MouseEvent) {
    stopCardNavigation(event);
    primaryAction?.onClick(ad);
  }

  function handleMenuButtonClick(event: MouseEvent) {
    stopCardNavigation(event);
    toggleMenu();
  }

  function handleMenuActionClick(action: MyAdCardAction) {
    action.onClick(ad);
    toggleMenu(false);
  }

  function renderMenuItems(isSheet = false) {
    return menuActions.map(action => {
      const content = (
        <Typography.Body $variant="1">{action.label}</Typography.Body>
      );

      if (isSheet) {
        return (
          <S.SheetItem
            key={action.label}
            $color={action.color}
            onClick={() => handleMenuActionClick(action)}
          >
            {content}
          </S.SheetItem>
        );
      }

      return (
        <S.MenuItem
          key={action.label}
          onClick={() => handleMenuActionClick(action)}
        >
          <S.MenuItemText $color={action.color}>{action.label}</S.MenuItemText>
        </S.MenuItem>
      );
    });
  }

  return (
    <S.Actions onClick={stopCardNavigation}>
      {primaryAction && (
        <S.PrimaryButton
          appearance="secondaryLight"
          size="s"
          height={40}
          borderRadius={14}
          isFullWidth
          hasPadding={false}
          onClick={handlePrimaryClick}
        >
          <S.PrimaryButtonText>{primaryAction.label}</S.PrimaryButtonText>
        </S.PrimaryButton>
      )}

      {hasMenu && (
        <>
          <S.MenuButton
            $isMenuOpen={isMenuOpen}
            shape="square"
            height={40}
            borderRadius={14}
            onClick={handleMenuButtonClick}
            aria-label={t('common.actions')}
          >
            <MoreHorizontalIcon fill="#1f2937" />
          </S.MenuButton>

          {isDesktop ? (
            <S.Dropdown isOpen={isMenuOpen} onClose={() => toggleMenu(false)}>
              <S.Menu>{renderMenuItems()}</S.Menu>
            </S.Dropdown>
          ) : (
            <BottomSheet
              isOpen={isMenuOpen}
              close={() => toggleMenu(false)}
              title={t('common.actions')}
              isInPortal
            >
              <S.SheetContent>{renderMenuItems(true)}</S.SheetContent>
            </BottomSheet>
          )}
        </>
      )}
    </S.Actions>
  );
};
